import { createReducer } from '@reduxjs/toolkit';
import { getActionStringWithoutState } from '@redux/reduxUtils';
import { getLoginActions, getLogoutActions } from '../oauth/oauthActions';
import { addNewOrganizationAction, getAllOrganizationsAction, getOrganizationByIdAction, getOrganizationFeaturesAction, setOrganizationFeatureAction, setSelectedOrganizationAction, updateOrganizationAction, updateOrganizationLocationAction } from './organizationActions';
import { OrganizationState } from './organizationTypes';

const SELECTED_ORG_STORAGE_KEY = 'selectedOrganizationId';

// Load selected organization from localStorage
export const loadSelectedOrganizationId = (): Nullable<string> => {
  try {
    const storedValue = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    return storedValue || null;
  } catch (error) {
    return null;
  }
};

// Save selected organization to localStorage
const saveSelectedOrganizationId = (organizationId: Nullable<string>): void => {
  try {
    if (organizationId) {
      localStorage.setItem(SELECTED_ORG_STORAGE_KEY, organizationId);
    } else {
      localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore localStorage errors
  }
};

export const initialOrganizationState: OrganizationState = {
  organizationById: {},
  organizationFeatures: [],
  isLoading: {},
  error: {},
  success: {},
  selectedOrganizationId: loadSelectedOrganizationId()
};

export default createReducer(initialOrganizationState, (builder) => {
  builder.addCase(addNewOrganizationAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(addNewOrganizationAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    if (action.payload?.id) {
      state.organizationById[action.payload.id] = action.payload;
    }
  });
  builder.addCase(addNewOrganizationAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(updateOrganizationAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(updateOrganizationAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    if (action.payload?.id) {
      state.organizationById[action.payload.id] = action.payload;
    }
  });
  builder.addCase(updateOrganizationAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(getOrganizationByIdAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(getOrganizationByIdAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    if (action.payload?.id) {
      state.organizationById[action.payload.id] = action.payload;
    }
  });
  builder.addCase(getOrganizationByIdAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(getAllOrganizationsAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(getAllOrganizationsAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    action.payload?.forEach((organization) => {
      state.organizationById[organization.id] = organization;
    });
  });
  builder.addCase(getAllOrganizationsAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(getOrganizationFeaturesAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(getOrganizationFeaturesAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    state.organizationFeatures = action.payload || [];
  });
  builder.addCase(getOrganizationFeaturesAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(setOrganizationFeatureAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(setOrganizationFeatureAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    // Update the feature in the array
    if (action.payload) {
      const index = state.organizationFeatures.findIndex(
        (f) => f.organizationId === action.payload.organizationId && f.featureId === action.payload.featureId
      );
      if (index !== -1) {
        state.organizationFeatures[index] = action.payload;
      } else {
        state.organizationFeatures.push(action.payload);
      }
    }
  });
  builder.addCase(setOrganizationFeatureAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(updateOrganizationLocationAction.start, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = true;
    delete state.error[tag];
  });
  builder.addCase(updateOrganizationLocationAction.success, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    delete state.error[tag];
    state.success[tag] = true;
    if (action.payload?.id) {
      state.organizationById[action.payload.id] = action.payload;
    }
  });
  builder.addCase(updateOrganizationLocationAction.fail, (state, action) => {
    const tag = getActionStringWithoutState(action.type);
    state.isLoading[tag] = false;
    state.error[tag] = {
      message: action.payload?.message,
      status: action.payload?.status
    };
  });
  builder.addCase(getLoginActions.success, (state, action) => {
    if (action.payload.organization) {
      state.organizationById[action.payload.organization.id] = action.payload.organization;
    }
  });
  builder.addCase(setSelectedOrganizationAction, (state, action) => {
    state.selectedOrganizationId = action.payload;
    saveSelectedOrganizationId(action.payload);
  });
  builder.addCase(getLogoutActions.success, () => {
    // Reset to initial state on logout to clear all cached organization data
    saveSelectedOrganizationId(null);
    return initialOrganizationState;
  });
});
